import type { PodcastData } from './interfaces';

import { Option, OptionGroup, Select } from '@/ui-components/atoms/Inputs/Select';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';

import { ChannelStripSkeleton } from '@/ui-components/molecules/ChannelStripSkeleton/ChannelStripSkeleton';

import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';

import styles from './podcastlist.module.scss';
import cn from 'classnames';
import { useMemo } from 'react';
import PodcastStripGroup from '../AssetGroup/Group/PodcastStripGroup';

interface PodcastFeedProps {
    podcasts: Array<PodcastData>;
    categorySelect: {
        value: string | undefined;
        onChange: (value: string | undefined) => void;
        options: Array<{ label: string; value: string }>;
    };
    defaultChannelImage: {
        url: string;
        altText: string;
    };
    isLoading?: boolean;
    onBottomReached?: () => void;
    disclaimer?: React.ReactNode;
    hasStickyBar?: boolean;
}

const ALL_OPTION = {
    label: 'All Categories',
    value: 'allCategories',
};

export function PodcastList(props: PodcastFeedProps) {
    const loadingRef = useIntersectionObserver<HTMLDivElement>(props.onBottomReached, {
        rootMargin: '0px 0px 150px 0px',
        threshold: 0.5,
    });
    const categoryOptions = [ALL_OPTION, ...props.categorySelect.options];

    const categoryLabel =
        props.categorySelect.options.find((category) => category.value === props.categorySelect.value)?.label ||
        ALL_OPTION.label;

    const sitePodcasts: Array<SitePodcast> = useMemo(() => {
        return props.podcasts.map((podcast) => {
            const latestEpisode = podcast.showSchedule?.latestEpisode;
            const episodeImage : string | undefined = latestEpisode?.image ? latestEpisode.image : podcast.showSchedule?.image;
            const episode: PodcastEpisode | undefined = latestEpisode
                ? {
                      episode_id: latestEpisode.entityID,
                      name: latestEpisode.name,
                      originalAirTime: latestEpisode.originalAirTime,
                      duration: latestEpisode.duration,
                      link: {
                          cms_id: latestEpisode.entityID,
                          cms_type: 'Link',
                          fields: { name: latestEpisode.name, href: latestEpisode.link },
                      },
                      image: episodeImage
                          ? ({ url: episodeImage, altText: latestEpisode.name } as AEMImage)
                          : undefined,
                  }
                : undefined;

            return {
                cms_id: podcast.assetId,
                cms_type: 'Podcast',
                fields: {
                    name: podcast.title,
                    title: podcast.title,
                    short_description: podcast.shortDescription,
                    note_text: podcast.noteText,
                    link: podcast.link
                        ? {
                              cms_id: podcast.assetId,
                              cms_type: 'Link',
                              fields: { name: podcast.title, href: podcast.link },
                          }
                        : undefined,
                    web_2_0_image: podcast.web_2_0_image,
                    show_id: podcast.showId,
                    episode,
                    genre: undefined,
                },
            };
        });
    }, [props.podcasts]);

    return (
        <Grid>
            <div className={styles.podcastListContainer}>
                <div
                    className={cn(styles.categorySelectContainer, {
                        [styles.hasStickyBar]: props.hasStickyBar,
                    })}
                >
                    <Select
                        className={styles.categorySelect}
                        type="dropdown"
                        label="Categories"
                        theme="grayLightWeb2"
                        name="Cate"
                        value={props.categorySelect.value || ALL_OPTION.value}
                        selectHeader={{
                            title: 'Select a category',
                            body: null,
                        }}
                        placeholder={categoryLabel}
                        onChange={(e) => {
                            window?.scrollTo(0, 0);
                            const value = e.value !== ALL_OPTION.value ? e.value : undefined;
                            props.categorySelect.onChange(value);
                        }}
                    >
                        <OptionGroup>
                            {categoryOptions.map((categoryOption) => {
                                if (categoryOption.label && categoryOption.value) {
                                    return (
                                        <Option key={categoryOption.value} value={categoryOption.value}>
                                            {categoryOption.label}
                                        </Option>
                                    );
                                }

                                return null;
                            })}
                        </OptionGroup>
                    </Select>
                </div>
                {props.disclaimer && <div className={styles.disclaimer}>{props.disclaimer}</div>}
                <div className={styles.podcastList}>
                    <PodcastStripGroup fallbackImageUrl={props.defaultChannelImage.url} podcasts={sitePodcasts} />
                    {props.isLoading && Array.from({ length: 20 }).map((_, i) => <ChannelStripSkeleton key={i} />)}
                </div>
                <div ref={loadingRef} />
            </div>
        </Grid>
    );
}
